
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, Container, Row, Spacer, Wordmark } from '@client/shared/design';
import { initials } from '@client/shared/lib/initials.ts';
import { useBreakpoint } from '@client/shared/hooks/use-media-query.ts';
import { useNavigate } from '@client/shared/router/router.tsx';
import { PATHS } from '@client/shared/router/paths.ts';
import { useLogout, type SessionState } from '@client/shared/auth/index.ts';
import type { Role } from '@client/shared/auth/session.ts';
import styles from './AppShell.module.css';

interface NavItem {
  label: string;
  href: string;
  roles: Role[];
}

// visibleNav matches held roles literally (no ladder), so member items list the whole
// ladder. A billing-only session sees none of these — its nav is the group-settings chip.
const MEMBER_ROLES: Role[] = ['agent', 'admin', 'owner'];

const NAV: NavItem[] = [
  { label: 'Home', href: PATHS.home, roles: MEMBER_ROLES },
  { label: 'Listings', href: PATHS.listings, roles: MEMBER_ROLES },
  { label: 'Bookings', href: PATHS.bookings, roles: MEMBER_ROLES },
  { label: 'Users', href: PATHS.users, roles: ['admin', 'owner'] },
];

const ACCOUNT_PATH = PATHS.account;

function visibleNav(roles: readonly Role[]): NavItem[] {
  return NAV.filter((item) => item.roles.length === 0 || item.roles.some((r) => roles.includes(r)));
}

export interface AppShellProps {
  session: Extract<SessionState, { status: 'authenticated' }>['session'];
  currentPath?: string;
  children?: ReactNode;
}

export function AppShell({ session, currentPath = '/', children }: AppShellProps) {
  const logout = useLogout();
  const navigate = useNavigate();
  const items = visibleNav(session.roles);
  const canManageGroup = session.roles.some(
    (r) => r === 'admin' || r === 'owner' || r === 'billing',
  );

  const signOut = useCallback(async () => {
    await logout().catch(() => {});
    navigate(PATHS.login, { replace: true });
  }, [navigate, logout]);

  const tier = useBreakpoint();
  const compact = tier === 'phone' || tier === 'tablet';

  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerId = useId();
  const drawerOpen = compact && menuOpen;

  useEffect(() => {
    if (!compact) setMenuOpen(false);
  }, [compact]);

  useEffect(() => {
    setMenuOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      toggleRef.current?.focus();
    };
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (toggleRef.current?.contains(t) || drawerRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDocDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDocDown);
    };
  }, [drawerOpen]);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <Container size="wide">
          <div className={styles.headerInner}>
            <a className={styles.brand} href="/" aria-label="Cheddule home">
              <Wordmark height={26} alt="" className={styles.brandImg} />
            </a>

            {!compact && (
              <nav className={styles.nav} aria-label="Primary">
                {items.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className={styles.navLink}
                    data-active={currentPath === item.href}
                    aria-current={currentPath === item.href ? 'page' : undefined}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            )}

            <Spacer />

            <Row gap={3} className={styles.account}>
              {canManageGroup ? (
                <a
                  href={PATHS.groupSettings}
                  className={styles.groupChip}
                  data-active={currentPath === PATHS.groupSettings}
                  aria-current={currentPath === PATHS.groupSettings ? 'page' : undefined}
                  aria-label={`Group settings — ${session.group.name}`}
                >
                  <GroupGlyph />
                  <span className={styles.groupName}>{session.group.name}</span>
                </a>
              ) : (
                <span
                  className={styles.groupChip}
                  data-static="true"
                  aria-label={`Your group — ${session.group.name}`}
                >
                  <GroupGlyph />
                  <span className={styles.groupName}>{session.group.name}</span>
                </span>
              )}
              <a
                href={ACCOUNT_PATH}
                className={styles.accountLink}
                data-active={currentPath === ACCOUNT_PATH}
                aria-current={currentPath === ACCOUNT_PATH ? 'page' : undefined}
                aria-label="My account"
              >
                <span className={styles.identity}>
                  <span className={styles.identityName}>{session.user.name}</span>
                  <span className={styles.identityMeta}>{session.user.email}</span>
                </span>
                <span className={styles.avatar} aria-hidden="true">
                  {initials(session.user.name)}
                </span>
              </a>
              {!compact && (
                <Button variant="ghost" size="sm" onClick={signOut}>
                  Sign out
                </Button>
              )}
            </Row>

            {compact && (
              <button
                ref={toggleRef}
                type="button"
                className={styles.menuToggle}
                aria-expanded={drawerOpen}
                aria-controls={drawerOpen ? drawerId : undefined}
                data-open={drawerOpen || undefined}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span className={styles.menuGlyph} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                Menu
              </button>
            )}
          </div>
        </Container>
      </header>

      {drawerOpen && (
        <>
          <div className={styles.drawerScrim} aria-hidden="true" />
          <div ref={drawerRef} id={drawerId} className={styles.drawer}>
            <Container size="wide">
              <nav className={styles.drawerNav} aria-label="Primary">
                {items.map((item, i) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className={styles.drawerLink}
                    style={{ ['--i' as string]: i }}
                    data-active={currentPath === item.href}
                    aria-current={currentPath === item.href ? 'page' : undefined}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className={styles.drawerIndex} aria-hidden="true">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {item.label}
                  </a>
                ))}
              </nav>
              <div className={styles.drawerFooter} style={{ ['--i' as string]: items.length }}>
                <span className={styles.drawerWho}>
                  <span className={styles.drawerWhoName}>{session.user.name}</span>
                  <span className={styles.drawerWhoMeta}>{session.user.email}</span>
                </span>
                <Button variant="ghost" size="sm" onClick={signOut}>
                  Sign out
                </Button>
              </div>
            </Container>
          </div>
        </>
      )}

      <main className={styles.main}>
        <Container size="wide">{children}</Container>
      </main>
    </div>
  );
}

function GroupGlyph() {
  return (
    <svg
      className={styles.groupGlyph}
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21h18" />
      <path d="M6 21V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16" />
      <path d="M15 9h3a1 1 0 0 1 1 1v11" />
      <path d="M9 8h1M9 12h1M11 8h1M11 12h1" />
    </svg>
  );
}
